import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, SearchableSelect, useToast, type SearchableSelectOption } from '@kss/ui';
import { apiClient } from '@kss/api';
import { ArrowLeft, Plus, Printer, Save, Send, Share2, Trash2, X } from 'lucide-react';
import { printInvoiceByFormat, type InvoicePrintInput, type TenantHeader } from '../../print/templates';
import { getAssignedFormat } from '../../print/formats';
import { getTenantHeaderInlined, getPrintBrandingInlined, printQrUrl } from '../../print/branding';
import { EntityPicker } from '../sales/EntityPicker';
import { compactDescEnvelope } from '../../utils/descEnvelope';
import { postLinesSequential, createHeaderAndGetId } from '../../utils/postLines';

/**
 * Purchase Order composer.
 *
 * Mirrors the SalesInvoice composer but supplier-side: pick a supplier,
 * add line items (product picker, qty, expected rate, GST class), and
 * either save as Draft or "Save & send" (Posted). Totals are calculated
 * the same way (subtotal → discount → taxable → CGST/SGST or IGST → grand
 * total) using the supplier's GSTIN to derive interstate vs intrastate.
 */

interface Supplier extends Record<string, unknown> {
  supplierId?: string;
  id?: number;
  code?: string;
  name?: string;
  gstin?: string;
  state?: string;
  paymentTerms?: string;
}

interface Product extends Record<string, unknown> {
  productId?: string;
  id?: number;
  code?: string;
  name?: string;
  hsnCode?: string;
  costPrice?: number;
  sellingPrice?: number;
  taxClassId?: string;
}

interface TaxClass extends Record<string, unknown> {
  taxClassId?: string;
  id?: number;
  rate?: number;
}

interface Line {
  key: string;
  productId: string | number | null;
  productLabel: string;
  hsnCode: string;
  qty: number;
  rate: number;
  discountPct: number;
  taxRate: number;
}

const STATE_CODE_MAP: Record<string, string> = {
  '01': 'Jammu & Kashmir', '02': 'Himachal Pradesh', '03': 'Punjab', '04': 'Chandigarh',
  '05': 'Uttarakhand', '06': 'Haryana', '07': 'Delhi', '08': 'Rajasthan',
  '09': 'Uttar Pradesh', '10': 'Bihar', '11': 'Sikkim', '12': 'Arunachal Pradesh',
  '13': 'Nagaland', '14': 'Manipur', '15': 'Mizoram', '16': 'Tripura',
  '17': 'Meghalaya', '18': 'Assam', '19': 'West Bengal', '20': 'Jharkhand',
  '21': 'Odisha', '22': 'Chhattisgarh', '23': 'Madhya Pradesh', '24': 'Gujarat',
  '27': 'Maharashtra', '29': 'Karnataka', '32': 'Kerala', '33': 'Tamil Nadu',
  '36': 'Telangana', '37': 'Andhra Pradesh',
};

const DEFAULT_PLACE_OF_SUPPLY = '27';

const inputCls =
  'h-9 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const textareaCls =
  'min-h-[60px] w-full rounded-md border border-border bg-surface-card px-3 py-2 text-sm text-content-primary outline-none focus:border-primary';

export default function PurchaseOrderComposer() {
  const { id: rawId } = useParams<{ id?: string }>();
  const isEdit = !!rawId && rawId !== 'new';
  const navigate = useNavigate();
  const toast = useToast();

  const [poNumber, setPoNumber] = useState(isEdit ? '' : 'PO-' + Date.now().toString(36).toUpperCase().slice(-6));
  // Replace the random stamp with a sequential PO-YYMMDD-NNNN number (scans
  // existing POs for today's max sequence), mirroring the sales-invoice format.
  useEffect(() => {
    if (isEdit) return;
    let cancelled = false;
    void (async () => {
      try {
        const ymd = new Date().toISOString().slice(2, 10).replace(/-/g, '');
        const pfx = `PO-${ymd}-`;
        const d = (await apiClient.get('/purchase/PurchaseOrder')).data;
        const rows = (Array.isArray(d) ? d : (d?.items ?? d?.data ?? [])) as Array<Record<string, unknown>>;
        let mx = 0;
        for (const r of rows) {
          const num = String(r.orderNumber ?? r.code ?? '');
          if (num.startsWith(pfx)) { const n = parseInt(num.slice(pfx.length), 10); if (Number.isFinite(n) && n > mx) mx = n; }
        }
        if (!cancelled) setPoNumber(`${pfx}${String(mx + 1).padStart(4, '0')}`);
      } catch { /* keep the random fallback */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit]);
  const [savedCode, setSavedCode] = useState('');
  const [poDate, setPoDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [expectedDate, setExpectedDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 10);
  });
  const [paymentTerms, setPaymentTerms] = useState('Net 30');
  const [shipTo, setShipTo] = useState('');
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState('Quoted prices include all taxes. Delivery as per agreed date.');

  const [supplier, setSupplier] = useState<Supplier | null>(null);
  // Bumped after an inline quick-add so the picker re-fetches its list.
  const [supplierReload, setSupplierReload] = useState(0);

  // Quick-add supplier (name + mobile only).
  const [qsOpen, setQsOpen] = useState(false);
  const [qsName, setQsName] = useState('');
  const [qsPhone, setQsPhone] = useState('');
  const [qsBusy, setQsBusy] = useState(false);

  // Ship-to: Warehouse / Branch dropdown that auto-fills the address text.
  const [shipToId, setShipToId] = useState('');
  const [warehouses, setWarehouses] = useState<Array<Record<string, unknown>>>([]);
  const [branches, setBranches] = useState<Array<Record<string, unknown>>>([]);
  useEffect(() => {
    void (async () => {
      const pick = (d: unknown) => (Array.isArray(d) ? d : ((d as { items?: unknown[]; data?: unknown[] })?.items ?? (d as { data?: unknown[] })?.data ?? [])) as Array<Record<string, unknown>>;
      try { const w = await apiClient.get('/master-data/Warehouse'); setWarehouses(pick(w.data)); } catch { /* */ }
      try { const b = await apiClient.get('/tenancy/Branch'); setBranches(pick(b.data)); } catch { /* */ }
    })();
  }, []);
  const shipToOptions: SearchableSelectOption[] = useMemo(() => {
    const fmtAddr = (r: Record<string, unknown>) =>
      [r.address, r.city, r.state, r.pincode ?? r.pinCode, r.country].filter(Boolean).join(', ');
    return [
      ...warehouses.map((w) => ({
        value: `W:${w.id ?? w.code}`,
        label: `🏬 ${String(w.name ?? w.code)}${fmtAddr(w) ? ' — ' + fmtAddr(w) : ''}`,
      })),
      ...branches.map((b) => ({
        value: `B:${b.id ?? b.code}`,
        label: `🏢 ${String(b.name ?? b.code)}${fmtAddr(b) ? ' — ' + fmtAddr(b) : ''}`,
      })),
    ].filter((o) => !o.value.endsWith('undefined'));
  }, [warehouses, branches]);

  // When a ship-to location is picked, fill the shipTo address text from it.
  function pickShipTo(val: string) {
    setShipToId(val);
    const [kind, id] = val.split(':');
    const pool = kind === 'W' ? warehouses : branches;
    const row = pool.find((r) => String(r.id ?? r.code) === id);
    if (row) {
      const addr = [row.name, row.address, row.city, row.state, row.pincode ?? row.pinCode, row.country]
        .filter(Boolean).join(', ');
      setShipTo(addr);
    }
  }

  async function quickAddSupplier() {
    if (!qsName.trim()) { toast.error('Name required'); return; }
    setQsBusy(true);
    try {
      const code = 'SUP-' + Date.now().toString(36).toUpperCase().slice(-6);
      const r = await apiClient.post('/purchase/Supplier', {
        code, name: qsName.trim(), description: '', status: 0, remarks: '',
        supplierType: 'Vendor', phone: qsPhone.trim(), email: '', gstin: '', pan: '',
        address: '', city: '', state: '', pinCode: '', country: 'India', currency: 'INR',
        paymentTermDays: 30, outstandingBalance: 0,
      });
      const created = (r.data?.data ?? r.data) as Supplier;
      setSupplier({ supplierId: String((created as { id?: number }).id ?? code), id: (created as { id?: number }).id, name: qsName.trim(), phone: qsPhone.trim() } as Supplier);
      setSupplierReload((k) => k + 1);
      setQsOpen(false); setQsName(''); setQsPhone('');
      toast.success('Supplier added', qsName.trim());
    } catch (e) {
      toast.error('Quick add failed', e instanceof Error ? e.message : 'Try again');
    } finally { setQsBusy(false); }
  }

  const [lines, setLines] = useState<Line[]>([
    { key: rid(), productId: null, productLabel: '', hsnCode: '', qty: 1, rate: 0, discountPct: 0, taxRate: 18 },
  ]);

  const [taxClasses, setTaxClasses] = useState<TaxClass[]>([]);
  useEffect(() => {
    apiClient
      .get('/master-data/TaxClass')
      .then((r) => {
        const d = r.data;
        const list = Array.isArray(d) ? d : (d?.items ?? d?.data ?? []);
        setTaxClasses(Array.isArray(list) ? list : []);
      })
      .catch(() => setTaxClasses([]));
  }, []);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState<'draft' | 'post' | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!isEdit || !rawId) return;
    setLoading(true);
    void (async () => {
      try {
        const r = await apiClient.get(`/purchase/PurchaseOrder/${encodeURIComponent(rawId)}`);
        const d = (r.data?.data ?? r.data) as Record<string, unknown> | undefined;
        if (!d || typeof d !== 'object') return;
        setSavedCode(String(d.code ?? ''));
        setPoNumber(String(d.orderNumber ?? d.poNumber ?? d.code ?? ''));
        setPoDate(String(d.orderDate ?? d.poDate ?? '').slice(0, 10) || poDate);
        setExpectedDate(String(d.expectedDeliveryDate ?? d.expectedDate ?? '').slice(0, 10) || expectedDate);
        if (d.paymentTermDays != null) setPaymentTerms(`Net ${d.paymentTermDays}`);
        setShipTo(String(d.shipTo ?? d.deliveryAddress ?? ''));
        setNotes(String(d.notes ?? ''));
        setTerms(String(d.terms ?? terms));
        if (d.supplierId != null) {
          setSupplier({ supplierId: String(d.supplierId), id: Number(d.supplierId), name: String(d.supplierName ?? ''), gstin: String(d.supplierGstin ?? '') } as Supplier);
        }
        // Lines from the SEPARATE PurchaseOrderLine table (keyed by purchaseOrderId).
        const lr = pickList((await apiClient.get('/purchase/PurchaseOrderLine')).data)
          .filter((x) => String(x.purchaseOrderId) === String(d.id))
          .sort((a, b) => Number(a.lineNumber ?? 0) - Number(b.lineNumber ?? 0));
        if (lr.length) {
          setLines([
            ...lr.map((l) => {
              const qty = Number(l.quantity ?? 1) || 1;
              const rate = Number(l.unitPrice ?? 0);
              const gross = qty * rate;
              const discountPct = gross > 0 ? round2((Number(l.lineDiscount ?? 0) / gross) * 100) : 0;
              return {
                key: rid(),
                productId: (l.productId ?? null) as number | null,
                productLabel: String(l.productName ?? l.name ?? ''),
                hsnCode: String(l.hsnCode ?? ''),
                qty, rate,
                discountPct: Number.isFinite(discountPct) ? Math.max(0, discountPct) : 0,
                taxRate: Number(l.taxRate ?? 18),
              };
            }),
            { key: rid(), productId: null, productLabel: '', hsnCode: '', qty: 1, rate: 0, discountPct: 0, taxRate: 18 },
          ]);
        } else {
          // No PurchaseOrderLine rows — recover the items from the header
          // `description` JSON (POs whose line POSTs failed keep them there).
          try {
            const env = JSON.parse(String(d.description ?? '{}')) as { lines?: Array<Record<string, unknown>>; items?: Array<Record<string, unknown>> };
            const src = (Array.isArray(env.lines) && env.lines.length ? env.lines : (Array.isArray(env.items) ? env.items : []));
            if (src.length) {
              setLines([
                ...src.map((l) => ({
                  key: rid(),
                  productId: (l.productId ?? null) as number | null,
                  productLabel: String(l.productName ?? l.name ?? l.itemName ?? ''),
                  hsnCode: String(l.hsnCode ?? ''),
                  qty: Number(l.quantity ?? l.qty ?? 1) || 1,
                  rate: Number(l.unitPrice ?? l.rate ?? l.price ?? 0),
                  discountPct: Number(l.discountPct ?? 0),
                  taxRate: Number(l.taxRate ?? 18),
                })),
                { key: rid(), productId: null, productLabel: '', hsnCode: '', qty: 1, rate: 0, discountPct: 0, taxRate: 18 },
              ]);
            }
          } catch { /* no description envelope */ }
        }
      } catch (e) {
        setErr(e instanceof Error ? e.message : 'Load failed');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawId]);

  const supplierState = useMemo(() => String(supplier?.gstin ?? '').slice(0, 2), [supplier]);
  const isInterstate = useMemo(
    () => !!supplierState && supplierState !== DEFAULT_PLACE_OF_SUPPLY,
    [supplierState],
  );

  const totals = useMemo(() => {
    let subtotal = 0;
    let totalDiscount = 0;
    let totalTax = 0;
    const lineTotals: { taxable: number; tax: number; total: number }[] = [];
    for (const l of lines) {
      const gross = (Number(l.qty) || 0) * (Number(l.rate) || 0);
      const discount = gross * ((Number(l.discountPct) || 0) / 100);
      const taxable = gross - discount;
      const tax = taxable * ((Number(l.taxRate) || 0) / 100);
      subtotal += gross;
      totalDiscount += discount;
      totalTax += tax;
      lineTotals.push({ taxable, tax, total: taxable + tax });
    }
    const taxable = subtotal - totalDiscount;
    const grand = taxable + totalTax;
    const rounded = Math.round(grand);
    const roundOff = Number((rounded - grand).toFixed(2));
    return {
      subtotal,
      totalDiscount,
      taxable,
      totalTax,
      cgst: isInterstate ? 0 : totalTax / 2,
      sgst: isInterstate ? 0 : totalTax / 2,
      igst: isInterstate ? totalTax : 0,
      roundOff,
      grand: rounded,
      lineTotals,
    };
  }, [lines, isInterstate]);

  function updateLine(idx: number, patch: Partial<Line>) {
    setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  }
  function addLine() {
    setLines((ls) => [
      ...ls,
      { key: rid(), productId: null, productLabel: '', hsnCode: '', qty: 1, rate: 0, discountPct: 0, taxRate: 18 },
    ]);
  }
  function removeLine(idx: number) {
    setLines((ls) => (ls.length === 1 ? ls : ls.filter((_, i) => i !== idx)));
  }
  function pickProduct(idx: number, p: Product | null) {
    if (!p) {
      updateLine(idx, { productId: null, productLabel: '', hsnCode: '' });
      return;
    }
    const taxClass = taxClasses.find(
      (t) => String(t.taxClassId ?? t.id) === String(p.taxClassId),
    );
    const taxRate = Number(taxClass?.rate ?? 18);
    // Purchase rate: prefer cost price, then the live `price` (§35), then
    // selling price / MRP. (The old code only read costPrice/sellingPrice,
    // which are absent on the live Product schema → rate stayed 0.)
    const pr = p as { costPrice?: number; price?: number; sellingPrice?: number; mrp?: number };
    const rate = Number(pr.costPrice ?? pr.price ?? pr.sellingPrice ?? pr.mrp ?? 0);
    updateLine(idx, {
      productId: p.productId ?? p.id ?? null,
      productLabel: p.name ?? p.code ?? '',
      hsnCode: String(p.hsnCode ?? ''),
      rate,
      taxRate,
    });
    // Auto-append a fresh blank line when the last row gets a product, so the
    // cashier can keep adding items without clicking "Add line".
    setLines((ls) => (idx === ls.length - 1
      ? [...ls, { key: rid(), productId: null, productLabel: '', hsnCode: '', qty: 1, rate: 0, discountPct: 0, taxRate: 18 }]
      : ls));
  }

  function validate(status: 'Draft' | 'Posted'): string | null {
    if (!supplier) return 'Pick a supplier first.';
    if (status === 'Posted') {
      // A posted PO must have at least one complete line. Validate only the
      // REAL lines — pickProduct auto-appends a blank trailing row, so looping
      // over every `lines` entry would always trip on that empty row and block
      // "Save & send". This matches how save() filters real lines before POST.
      const real = lines.filter((l) => l.productLabel.trim() || l.productId != null);
      if (real.length === 0) return 'Add at least one line item.';
      for (const [i, l] of real.entries()) {
        if (!l.productLabel) return `Line ${i + 1}: select a product.`;
        if (!Number.isFinite(Number(l.qty)) || Number(l.qty) <= 0) return `Line ${i + 1}: qty must be > 0.`;
        if (!Number.isFinite(Number(l.rate)) || Number(l.rate) < 0) return `Line ${i + 1}: rate must be ≥ 0.`;
      }
    } else {
      // A DRAFT can have zero lines (work-in-progress). Only validate rows the
      // user has actually started filling in.
      for (const [i, l] of lines.entries()) {
        if (!l.productLabel) continue;
        if (!Number.isFinite(Number(l.qty)) || Number(l.qty) <= 0) return `Line ${i + 1}: qty must be > 0.`;
        if (!Number.isFinite(Number(l.rate)) || Number(l.rate) < 0) return `Line ${i + 1}: rate must be ≥ 0.`;
      }
    }
    return null;
  }

  async function save(status: 'Draft' | 'Posted') {
    const e = validate(status);
    if (e) {
      setErr(e);
      toast.error('Fix errors', e);
      return;
    }
    setSaving(status === 'Draft' ? 'draft' : 'post');
    setErr(null);
    try {
      // §35: write the REAL PurchaseOrder columns (orderNumber / orderDate /
      // expectedDeliveryDate / supplierId / discountAmount / taxAmount /
      // grandTotal / orderStatus). The old payload used poNumber/poDate/
      // totalAmount which the backend ignored → list showed blank rows.
      const header: Record<string, unknown> = {
        code: poNumber, name: `PO ${poNumber}`, status: 0,
        orderNumber: poNumber,
        orderDate: poDate,
        expectedDeliveryDate: expectedDate,
        supplierId: supplier!.supplierId ?? supplier!.id,
        supplierName: supplier!.name,          // harmless extra
        supplierGstin: supplier!.gstin,
        placeOfSupply: supplierState || DEFAULT_PLACE_OF_SUPPLY,
        paymentTermDays: Number(String(paymentTerms).replace(/\D/g, '')) || 0,
        warehouseId: shipToId.startsWith('W:') ? Number(shipToId.slice(2)) || undefined : undefined,
        shipTo: shipTo || undefined,
        deliveryAddress: shipTo || undefined,
        notes: notes || undefined,
        terms: terms || undefined,
        currency: 'INR',
        orderStatus: status,
        subTotal: round2(totals.subtotal),
        discountAmount: round2(totals.totalDiscount),
        taxAmount: round2(totals.totalTax),
        shippingCharge: 0,
        roundOff: totals.roundOff,
        grandTotal: totals.grand,
        totalAmount: totals.grand,             // harmless extra
        // Recoverable fallback: a COMPACT, length-capped copy of the lines in
        // the header `description` (kept under the column's small limit so the
        // POST can't 500 with "string would be truncated").
        description: compactDescEnvelope(
          lines
            .filter((l) => l.productId != null || l.productLabel.trim() !== '')
            .map((l) => ({ name: l.productLabel, quantity: l.qty, unitPrice: l.rate, taxRate: l.taxRate, productId: l.productId })),
        ),
      };

      let poId: number | string | undefined;
      const idOf = (d: unknown): number | string | undefined => {
        const top = (d ?? {}) as Record<string, unknown>;
        const node = (top.data ?? top) as Record<string, unknown>;
        const v = node?.id;
        return typeof v === 'number' || typeof v === 'string' ? v : undefined;
      };
      if (isEdit && rawId) {
        const numId = Number(rawId);
        const idPart = Number.isFinite(numId) && numId > 0 ? { id: numId } : {};
        const r = await apiClient.put('/purchase/PurchaseOrder', { ...header, ...idPart, code: savedCode || poNumber || rawId });
        poId = idOf(r.data) ?? (Number.isFinite(numId) ? numId : undefined);
      } else {
        // Create POST returns 200 with an EMPTY body (no id) → recover by the
        // unique code, else the PurchaseOrderLine block below is skipped and the
        // PO saves with ZERO line rows.
        const { id } = await createHeaderAndGetId('/purchase/PurchaseOrder', header, {
          matchField: 'code',
          matchValue: poNumber,
        });
        poId = id;
      }

      // Lines → the SEPARATE PurchaseOrderLine table (nested `lines` on the
      // header are ignored). On edit, clear the old line rows first.
      const real = lines.filter((l) => l.productLabel.trim() || l.productId != null);
      if (poId != null) {
        if (isEdit) {
          try {
            const ex = pickList((await apiClient.get('/purchase/PurchaseOrderLine')).data).filter((x) => String(x.purchaseOrderId) === String(poId));
            await Promise.all(ex.map((x) => apiClient.delete(`/purchase/PurchaseOrderLine/${x.id}`).catch(() => undefined)));
          } catch { /* best-effort */ }
        }
        // Sequential so a parallel burst can't race the auth refresh and drop
        // lines (which left POs missing items).
        await postLinesSequential('/purchase/PurchaseOrderLine', real.map((l, i) => {
          const lt = totals.lineTotals[lines.indexOf(l)] ?? { taxable: 0, tax: 0, total: 0 };
          return {
            code: `${poNumber}-L${i + 1}`, name: l.productLabel || `Line ${i + 1}`, status: 0,
            purchaseOrderId: poId, lineNumber: i + 1,
            productId: l.productId ?? null, productName: l.productLabel, sku: '',
            quantity: Number(l.qty), receivedQuantity: 0, unitOfMeasure: 'PCS', unitPrice: Number(l.rate),
            lineDiscount: round2(Number(l.rate) * Number(l.qty) * Number(l.discountPct) / 100),
            taxRate: Number(l.taxRate), taxAmount: round2(lt.tax), lineTotal: round2(lt.total),
          };
        }));
      }
      toast.success('Saved', `PO ${status === 'Posted' ? 'sent' : isEdit ? 'updated' : 'saved as draft'}`);
      navigate('/panel/purchase/orders');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      setErr(msg);
      toast.error('Save failed', msg);
    } finally {
      setSaving(null);
    }
  }

  /** Share a short PO summary via the native share sheet (clipboard fallback). */
  async function doShare() {
    const real = lines.filter((l) => l.productLabel.trim() || l.productId != null);
    const text = [
      `Purchase Order ${poNumber}`,
      supplier?.name ? `Supplier: ${supplier.name}` : '',
      `Items: ${real.length}`,
      `Total: ₹${inr(totals.grand)}`,
    ].filter(Boolean).join('\n');
    try {
      const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void> };
      if (nav.share) { await nav.share({ title: `PO ${poNumber}`, text }); return; }
      await navigator.clipboard.writeText(text);
      toast.success('Copied', 'PO summary copied to clipboard');
    } catch { /* user dismissed the share sheet — ignore */ }
  }

  /** Print the purchase document using the admin-assigned format for the
   *  Purchase Invoice module (default: distinct A4 Purchase Invoice). */
  async function printPo() {
    // Tenant header + branding (logo/signature/QR/bank) come from /tenants/Tenant/me
    // (cached) with a Settings → Print Formats override.
    const tenant: TenantHeader = await getTenantHeaderInlined();
    const b = await getPrintBrandingInlined();
    const realLines = lines.filter((l) => l.productLabel?.trim() || l.productId != null);
    const input: InvoicePrintInput = {
      docType: 'PURCHASE INVOICE',
      number: poNumber || 'DRAFT',
      date: poDate || new Date().toISOString(),
      tenant,
      parties: [{
        label: 'Supplier',
        name: String(supplier?.name ?? '—'),
        gstin: supplier?.gstin ? String(supplier.gstin) : undefined,
        phone: supplier?.phone ? String(supplier.phone) : undefined,
      }],
      lines: realLines.map((l, i) => ({
        description: String(l.productLabel ?? ''),
        hsnCode: l.hsnCode ? String(l.hsnCode) : undefined,
        qty: Number(l.qty) || 0,
        rate: Number(l.rate) || 0,
        discountPct: Number(l.discountPct) || 0,
        taxRate: Number(l.taxRate) || 0,
        total: totals.lineTotals[lines.indexOf(l)]?.total ?? 0,
      })),
      subTotal: totals.subtotal,
      totalDiscount: totals.totalDiscount,
      taxableAmount: totals.taxable,
      cgstAmount: totals.cgst,
      sgstAmount: totals.sgst,
      igstAmount: totals.igst,
      roundOff: totals.roundOff,
      totalAmount: totals.grand,
      bank: (b.bankName || b.bankAccountNo) ? { name: b.bankName, accountName: b.bankAccountName, accountNo: b.bankAccountNo, ifsc: b.bankIfsc, branch: b.bankBranch } : undefined,
      signatureUrl: b.signatureUrl,
      qrUrl: printQrUrl(b.qrUrl, ['PURCHASE INVOICE', poNumber || '', String(supplier?.name ?? ''), 'Amt:' + totals.grand, tenant.gstin]),
    };
    printInvoiceByFormat(getAssignedFormat('purchaseInvoice'), input);
  }

  return (
    <div className="-m-6 space-y-3 p-[3px]">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface-card px-4 py-3">
        <Button variant="outline" size="sm" onClick={() => navigate('/panel/purchase/orders')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-semibold text-content-primary">
            {isEdit ? 'Edit purchase order' : 'New purchase order'}
          </h1>
          <p className="text-xs text-content-muted">
            {supplier
              ? `Order from ${supplier.name ?? '—'}${
                  supplier.gstin ? ` · GSTIN ${supplier.gstin}` : ''
                }${supplierState ? ` · ${STATE_CODE_MAP[supplierState] ?? supplierState}` : ''}${
                  supplier.gstin ? (isInterstate ? ' · IGST' : ' · CGST + SGST') : ''
                }`
              : 'Pick a supplier to start.'}
          </p>
        </div>
        <Button variant="outline" disabled={!!saving || loading || !supplier} onClick={() => void printPo()}>
          <Printer className="h-4 w-4" /> Print
        </Button>
        <Button variant="outline" disabled={!!saving || loading || !supplier} onClick={() => void doShare()}>
          <Share2 className="h-4 w-4" /> Share
        </Button>
        <Button variant="outline" disabled={!!saving || loading} onClick={() => save('Draft')}>
          <Save className="h-4 w-4" /> {saving === 'draft' ? 'Saving…' : 'Save draft'}
        </Button>
        <Button disabled={!!saving || loading} onClick={() => save('Posted')}>
          <Send className="h-4 w-4" /> {saving === 'post' ? 'Sending…' : 'Save & send'}
        </Button>
      </div>

      {err && <Alert variant="danger">{err}</Alert>}

      <Card>
        <div className="grid gap-3 md:grid-cols-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-content-secondary">PO number</span>
            <input
              className={inputCls}
              value={poNumber}
              onChange={(e) => setPoNumber(e.target.value)}
              placeholder="auto-generated if blank"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-content-secondary">PO date</span>
            <input type="date" className={inputCls} value={poDate} onChange={(e) => setPoDate(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-content-secondary">Expected delivery</span>
            <input
              type="date"
              className={inputCls}
              value={expectedDate}
              onChange={(e) => setExpectedDate(e.target.value)}
            />
          </label>
          <div className="flex flex-col gap-1 md:col-span-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-content-secondary">Supplier</span>
              <button
                type="button"
                onClick={() => setQsOpen(true)}
                className="text-[11px] font-medium text-primary hover:underline"
              >
                + Quick add (name + mobile)
              </button>
            </div>
            <EntityPicker<Supplier>
              basePath="/purchase/Supplier"
              idKey="supplierId"
              labelKey="name"
              secondaryKey="gstin"
              value={(supplier?.supplierId ?? supplier?.id) as string | number | null}
              selectedRow={supplier}
              reloadKey={supplierReload}
              onChange={(s) => {
                setSupplier(s);
                if (s?.paymentTerms) setPaymentTerms(String(s.paymentTerms));
              }}
              placeholder="Search supplier by name, code or GSTIN"
            />
          </div>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-content-secondary">Payment terms</span>
            <input
              className={inputCls}
              value={paymentTerms}
              onChange={(e) => setPaymentTerms(e.target.value)}
              placeholder="Net 30, Advance, COD…"
            />
          </label>
          <div className="flex flex-col gap-1 md:col-span-3">
            <span className="text-xs font-medium text-content-secondary">Ship to (warehouse / branch)</span>
            <SearchableSelect
              value={shipToId}
              onChange={pickShipTo}
              options={shipToOptions}
              placeholder="Pick a warehouse or branch to deliver to"
              searchPlaceholder="Search warehouse / branch…"
            />
            {shipTo && (
              <span className="mt-0.5 text-[11px] text-content-muted">Delivering to: {shipTo}</span>
            )}
          </div>
        </div>
      </Card>

      <Card>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-content-primary">Line items</h3>
          <Button size="sm" variant="outline" onClick={addLine}>
            <Plus className="h-4 w-4" /> Add line
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-content-muted">
              <tr className="border-b border-border">
                <th className="px-2 py-2 text-left">#</th>
                <th className="px-2 py-2 text-left">Product</th>
                <th className="px-2 py-2 text-left">HSN</th>
                <th className="px-2 py-2 text-right">Qty</th>
                <th className="px-2 py-2 text-right">Rate (₹)</th>
                <th className="px-2 py-2 text-right">Disc %</th>
                <th className="px-2 py-2 text-right">GST %</th>
                <th className="px-2 py-2 text-right">Taxable</th>
                <th className="px-2 py-2 text-right">Tax</th>
                <th className="px-2 py-2 text-right">Total</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => {
                const t = totals.lineTotals[i] ?? { taxable: 0, tax: 0, total: 0 };
                return (
                  <tr key={l.key} className="border-b border-border/40">
                    <td className="px-2 py-2 text-content-muted">{i + 1}</td>
                    <td className="px-2 py-2" style={{ minWidth: 220 }}>
                      <EntityPicker<Product>
                        basePath="/master-data/Product"
                        idKey="productId"
                        labelKey="name"
                        secondaryKey="code"
                        value={l.productId}
                        onChange={(p) => pickProduct(i, p)}
                        placeholder="Search product"
                        // Show the saved product name on edit without waiting for
                        // the on-focus product fetch (#10 — blank line names).
                        selectedRow={l.productId != null || l.productLabel
                          ? ({ productId: l.productId, id: l.productId, name: l.productLabel, code: l.productLabel } as unknown as Product)
                          : null}
                      />
                    </td>
                    <td className="px-2 py-2">
                      <input
                        className={inputCls}
                        value={l.hsnCode}
                        onChange={(e) => updateLine(i, { hsnCode: e.target.value.replace(/\D/g, '').slice(0, 8) })}
                        placeholder="HSN/SAC"
                        style={{ width: 90 }}
                      />
                    </td>
                    <td className="px-2 py-2 text-right">
                      <input
                        type="number"
                        className={`${inputCls} text-right`}
                        value={l.qty}
                        min={0}
                        step={0.001}
                        onChange={(e) => updateLine(i, { qty: Number(e.target.value) })}
                        style={{ width: 80 }}
                      />
                    </td>
                    <td className="px-2 py-2 text-right">
                      <input
                        type="number"
                        className={`${inputCls} text-right`}
                        value={l.rate}
                        min={0}
                        step={0.01}
                        onChange={(e) => updateLine(i, { rate: Number(e.target.value) })}
                        style={{ width: 100 }}
                      />
                    </td>
                    <td className="px-2 py-2 text-right">
                      <input
                        type="number"
                        className={`${inputCls} text-right`}
                        value={l.discountPct}
                        min={0}
                        max={100}
                        step={0.01}
                        onChange={(e) => updateLine(i, { discountPct: Number(e.target.value) })}
                        style={{ width: 70 }}
                      />
                    </td>
                    <td className="px-2 py-2 text-right">
                      <select
                        className={`${inputCls} text-right`}
                        value={l.taxRate}
                        onChange={(e) => updateLine(i, { taxRate: Number(e.target.value) })}
                        style={{ width: 80 }}
                      >
                        {[0, 0.25, 3, 5, 12, 18, 28].map((r) => (
                          <option key={r} value={r}>{r}%</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums">{inr(t.taxable)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{inr(t.tax)}</td>
                    <td className="px-2 py-2 text-right font-medium tabular-nums">{inr(t.total)}</td>
                    <td className="px-2 py-2">
                      <button
                        type="button"
                        className="text-content-muted hover:text-danger"
                        onClick={() => removeLine(i)}
                        aria-label="Remove line"
                        disabled={lines.length === 1}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="grid gap-3 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <div className="grid gap-3 md:grid-cols-2">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-content-secondary">Notes (visible to supplier)</span>
              <textarea className={textareaCls} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-content-secondary">Terms &amp; conditions</span>
              <textarea className={textareaCls} value={terms} onChange={(e) => setTerms(e.target.value)} />
            </label>
          </div>
        </Card>
        <Card>
          <h3 className="mb-3 text-sm font-semibold text-content-primary">Totals</h3>
          <Row k="Subtotal" v={inr(totals.subtotal)} />
          <Row k="Discount" v={`− ${inr(totals.totalDiscount)}`} />
          <Row k="Taxable amount" v={inr(totals.taxable)} bold />
          {isInterstate ? (
            <Row k="IGST" v={inr(totals.igst)} />
          ) : (
            <>
              <Row k="CGST" v={inr(totals.cgst)} />
              <Row k="SGST" v={inr(totals.sgst)} />
            </>
          )}
          <Row k="Round off" v={inr(totals.roundOff)} />
          <div className="mt-2 border-t border-border pt-2">
            <Row k="Grand total" v={`₹ ${inr(totals.grand)}`} big />
          </div>
        </Card>
      </div>

      {/* Quick-add supplier modal */}
      {qsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !qsBusy && setQsOpen(false)}>
          <div className="w-full max-w-sm rounded-lg bg-surface-card p-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-content-primary">Quick add supplier</h3>
              <button type="button" onClick={() => setQsOpen(false)} disabled={qsBusy} className="rounded p-1 text-content-muted hover:bg-surface-card-hover">
                <X size={16} />
              </button>
            </div>
            <div className="space-y-3">
              <label className="block">
                <span className="mb-1 block text-xs text-content-secondary">Name *</span>
                <input className={inputCls} value={qsName} onChange={(e) => setQsName(e.target.value)} placeholder="Supplier name" autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && void quickAddSupplier()} />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs text-content-secondary">Mobile number</span>
                <input className={inputCls} value={qsPhone} inputMode="numeric" maxLength={10}
                  onChange={(e) => setQsPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="10-digit mobile"
                  onKeyDown={(e) => e.key === 'Enter' && void quickAddSupplier()} />
              </label>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="ghost" size="sm" onClick={() => setQsOpen(false)} disabled={qsBusy}>Cancel</Button>
                <Button size="sm" onClick={() => void quickAddSupplier()} disabled={qsBusy || !qsName.trim()}>
                  {qsBusy ? 'Adding…' : 'Add + select'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ k, v, bold, big }: { k: string; v: string; bold?: boolean; big?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <span className={`text-xs ${big ? 'text-sm text-content-primary' : 'text-content-muted'}`}>{k}</span>
      <span
        className={`tabular-nums ${
          big ? 'text-base font-semibold text-content-primary' : bold ? 'font-medium' : 'text-content-primary'
        }`}
      >
        {v}
      </span>
    </div>
  );
}

function inr(n: number): string {
  if (!Number.isFinite(n)) return '0.00';
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function rid(): string {
  return Math.random().toString(36).slice(2, 9);
}

function pickList(d: unknown): Array<Record<string, unknown>> {
  if (Array.isArray(d)) return d as Array<Record<string, unknown>>;
  const o = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } } | null;
  if (Array.isArray(o?.items)) return o!.items as Array<Record<string, unknown>>;
  if (Array.isArray(o?.data)) return o!.data as Array<Record<string, unknown>>;
  if (o?.data && Array.isArray((o.data as { items?: unknown[] }).items)) return (o.data as { items: unknown[] }).items as Array<Record<string, unknown>>;
  return [];
}
